/**
 * SOLID Review
 * S — Strong. One concern: the round trip a move makes — plan, server, answer — with each decision in its own step of the
 *     serial section and the server called between them.
 * O — Strong. A new round trip is a use case supplying its three strategies; nothing here changes.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Three strategy roles a use case implements, and two ways to run.
 * D — Strong. The step it is handed, and the strategies of each call.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPayloadPortFailure } from '@ml3d/kernel';
import type { TPortOutcome } from '../ports/PortResult';
import type { TDecision, IServiceSerialStep } from './ClientSteps';

/** The plan: what to ask the server, decided on the client's state — or a refusal, and nothing is sent. */
interface IServiceTripPlan<TInput, TCommand, TPlan> {
  plan(ctx: IContextRequest, input: TInput, command: TCommand): Promise<TDecision<TPlan>>;
}

/** A call's answered branch, as its port types it: `{ success: true, value }` — a void op's `value` optional (`ResultVoid`). */
export type TAnswered<T> = { readonly success: true; readonly value: T };
export type TAnsweredVoid = { readonly success: true; readonly value?: void };
type TAnsweredAny = { readonly success: true };

/** The server call, made with the plan — outside the serial section. */
interface IServiceTripCall<TPlan, TAnswer extends TAnsweredAny> {
  send(ctx: IContextRequest, plan: TPlan): Promise<TAnswer | IPayloadPortFailure>;
}

/** The answer: what the server's answer does to the client's state, decided against the state as it is by then. */
interface IServiceTripAnswer<TInput, TPlan, TAnswer> {
  answer(ctx: IContextRequest, input: TInput, plan: TPlan, answer: TAnswer): Promise<TDecision<unknown>>;
}

/** A round trip's three strategies — what one round-trip use case supplies. */
export type TServerRoundTrip<TInput, TCommand, TPlan, TAnswer extends TAnsweredAny> =
  IServiceTripPlan<TInput, TCommand, TPlan> & IServiceTripCall<TPlan, TAnswer> & IServiceTripAnswer<TInput, TPlan, TAnswer>;
/** …for a round trip that answers a refused call too: its answer strategy is handed the call's whole result. */
export type TServerRoundTripToTheEnd<TInput, TCommand, TPlan, TAnswer extends TAnsweredAny> =
  IServiceTripPlan<TInput, TCommand, TPlan> & IServiceTripCall<TPlan, TAnswer> & IServiceTripAnswer<TInput, TPlan, TAnswer | IPayloadPortFailure>;

/**
 * THE ROUND TRIP (frontend-application-oo P2, 2026-10-28). Thirty-two use cases wrote the same sequence by hand — plan in a
 * step, call the server, answer in a step — each with its own copy of the stops between. It is written once, here:
 *
 *     step(plan)  →  server call  →  step(answer)
 *
 * Each decision runs in its OWN step of the one serial section (`ClientSteps`, FE1/FE2); the server is called BETWEEN them,
 * never inside one, so a slow server cannot hold the client — and the answer is decided against the state as it is when the
 * answer comes, which other steps may have changed meanwhile. A refused plan sends nothing and answers its refusal.
 *
 * Two ways to end. `run` stops at a refused call: the answer step has nothing to apply. `runToTheEnd` answers a refused call
 * too, for a move whose plan already moved the table (the lift clears the decider, Play Again restores the lives): its
 * answer strategy is handed the call's whole result, and the use case answers the call's refusal after the step ran.
 */
export class ServerRoundTrip<TInput> {
  constructor(private readonly step: IServiceSerialStep<TInput>) {}

  async run<TCommand, TPlan, TAnswer extends TAnsweredAny>(
    ctx: IContextRequest, command: TCommand, trip: TServerRoundTrip<TInput, TCommand, TPlan, TAnswer>,
  ): Promise<TPortOutcome> {
    const plan = await this.step.run(ctx, (input) => trip.plan(ctx, input, command));
    if (!plan.success) return plan;
    const sent = await trip.send(ctx, plan.value);
    if (ServerRoundTrip.refused(sent)) return sent;
    return this.step.outcome(await this.step.run(ctx, (input) => trip.answer(ctx, input, plan.value, sent)));
  }

  async runToTheEnd<TCommand, TPlan, TAnswer extends TAnsweredAny>(
    ctx: IContextRequest, command: TCommand, trip: TServerRoundTripToTheEnd<TInput, TCommand, TPlan, TAnswer>,
  ): Promise<TPortOutcome> {
    const plan = await this.step.run(ctx, (input) => trip.plan(ctx, input, command));
    if (!plan.success) return plan;
    const sent = await trip.send(ctx, plan.value);
    const after = await this.step.run(ctx, (input) => trip.answer(ctx, input, plan.value, sent));
    return ServerRoundTrip.refused(sent) ? sent : this.step.outcome(after);
  }

  /** A call's refusal — what the port answered when it did not answer. */
  private static refused(sent: TAnsweredAny | IPayloadPortFailure): sent is IPayloadPortFailure {
    return !sent.success;
  }
}
